<template>
  <Dialog
    v-model="model"
    :title="currentSlideAlert?.title ?? 'No Alert Title'"
    :show-close-button="false"
    persistent
    :q-card-props="{
      class: '!w-full !tablet:max-w-75vw tablet:max-h-75vh p-5',
    }"
  >
    <template #body>
      <div
        :data-cy="DATA_CY.ALERT_REVIEW_DIALOG"
        class="w-full flex justify-evenly gap-2 flex-col"
      >
        <div
          class="w-full max-h-80vh tablet:max-h-60vh min-h-450px overflow-auto"
        >
          <q-carousel
            ref="carouselRef"
            v-model="indexOfCurrentSlide"
            transition-prev="slide-right"
            transition-next="slide-left"
            animated
            control-color="primary"
            class="rounded-borders"
          >
            <q-carousel-slide
              v-for="(alert, alertIndex) in systemAlerts"
              :key="alert.systemAlertID"
              :name="alertIndex"
            >
              <div class="ul--normal" v-html="alert.body" />
            </q-carousel-slide>
          </q-carousel>
        </div>

        <div
          v-if="currentSlideAlert?.requireAcknowledgement"
          class="flex justify-end items-center"
        >
          <q-checkbox
            v-model="currentSlideAlert.isAcknowledged"
            :data-cy="DATA_CY.ALERT_REVIEW_CONFIRM_CHECKBOX"
            label="I acknowledge the policy"
          />
        </div>
        <div class="flex justify-between items-center">
          <BaseButton
            type="button"
            cta="Prev"
            :disabled="leftButtonDisabled"
            @click="prev"
          />
          <span :data-cy="DATA_CY.ALERT_REVIEW_PAGINATION">{{
            `${indexOfCurrentSlide ? indexOfCurrentSlide + 1 : '1'} of ${
              systemAlerts?.length ?? 0
            }`
          }}</span>
          <BaseButton
            type="button"
            :cta="rightButtonCta"
            :disabled="rightButtonDisabled"
            :loading="isLoading"
            :data-cy="DATA_CY.ALERT_REVIEW_BUTTON"
            @click="
              next(
                currentSlideAlert?.systemAlertID as string,
                currentSlideAlert?.requireAcknowledgement ? true : undefined
              )
            "
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { QCarousel } from 'quasar'
import type { QueriesAlertUnreadAlerts } from 'client'
import { DATA_CY } from '~/constants/strings'
import { useSystemAlertsClientStore } from '~/stores/systemAlertsClient'

interface ISystemAlertUnreadAlert extends QueriesAlertUnreadAlerts {
  isAcknowledged: boolean
  isViewed: boolean
}

const SystemAlertsClientStore = useSystemAlertsClientStore()

const p = defineProps<{ isOpen: boolean }>()
const emit = defineEmits(['update:isOpen'])
const model = computed({
  get: () => {
    return p.isOpen
  },
  set: () => {
    emit('update:isOpen')
  },
})

const systemAlerts = ref<ISystemAlertUnreadAlert[]>()

const isLoading = ref(false)
const carouselRef = ref<QCarousel>()
const currentSlideAlert = computed<ISystemAlertUnreadAlert | undefined>(() => {
  return systemAlerts.value?.[indexOfCurrentSlide.value]
})

const closeDialog = () => {
  model.value = false
}

const viewedAlertIds = computed(() => {
  return systemAlerts.value?.reduce<string[]>((acc, curr) => {
    if (curr.isViewed) {
      acc.push(curr.systemAlertID)
    }
    return acc
  }, [])
})

const handleNextSuccess = () => {
  if (currentSlideAlert.value) {
    currentSlideAlert.value.isViewed = true
  }

  if (isLastSlide.value) {
    closeDialog()
    return
  }

  carouselRef.value?.next()
  return
}

const next = async (id: string, isAcknowledged: true | undefined) => {
  try {
    isLoading.value = true
    if (viewedAlertIds.value?.includes(id)) {
      handleNextSuccess()
      return
    }

    const success = await SystemAlertsClientStore.acknowledgeAlert({
      alertId: id,
      isAcknowledged,
    })

    if (success) {
      handleNextSuccess()
    }
  } finally {
    isLoading.value = false
  }
}

const prev = () => {
  isLoading.value = true
  carouselRef.value?.previous()
  isLoading.value = false
}

const indexOfCurrentSlide = ref(0)

const isLastSlide = computed(() => {
  return (
    systemAlerts.value?.length &&
    indexOfCurrentSlide.value === systemAlerts.value.length - 1
  )
})

const leftButtonDisabled = computed(() => {
  const previousSlideExists =
    indexOfCurrentSlide.value &&
    systemAlerts.value?.[indexOfCurrentSlide.value - 1]
  return !previousSlideExists
})

const rightButtonDisabled = computed(() => {
  const currentAlertRequiresAcknowledgement =
    currentSlideAlert.value?.requireAcknowledgement
  const currentSlideAcknowledged = currentSlideAlert.value?.isAcknowledged

  if (currentAlertRequiresAcknowledgement && !currentSlideAcknowledged) {
    return true
  }

  return false
})

const rightButtonCta = computed(() => {
  if (isLastSlide.value) {
    return 'FINISH'
  }
  return 'NEXT'
})

const getSystemAlerts = () => {
  if (SystemAlertsClientStore.alerts.length > 0) {
    systemAlerts.value = SystemAlertsClientStore.alerts.map(
      (alert): ISystemAlertUnreadAlert => {
        return {
          ...alert,
          isAcknowledged: false,
          isViewed: false,
        }
      }
    )

    indexOfCurrentSlide.value = 0
  }
}

watchEffect(() => {
  if (model.value) {
    getSystemAlerts()
  }
})
</script>
